import React, { useEffect } from 'react';
import {Route, Router, Switch, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {fenceHistory} from '../../../stores/store';
import { setGeoFenceContainerShow, toggleGeoFenceContainerShow } from '../../../stores/reducers/fencesContainerUI-actions';

import {FenceGroup, iFullStoreState, ItemType} from '../../../shared/interfaces';
import {isMobile} from '../../../shared/helpers';
import {withSentry} from '../../../shared/hoc/withSentry';
import {useRedux} from '../../../states/redux-state';

import FenceBody from '../tab-bodies/FenceBody/FenceBody';
import FenceBodyLayout from '../tab-bodies/BodyLayout/FenceBodyLayout/FenceBodyLayout';
import FenceEdit from '../tab-bodies/EditFence/EditFence';
import FilterEdit from '../FilterEdit/FilterEdit';

import LoadFenceAsyncWrapper from "./ LoadFenceAsyncWrapper";
import MapEmptyBodyLayout from '../tab-bodies/BodyLayout/MapEmptyBodyLayout/MapEmptyBodyLayout';
import PathEdit from '../tab-bodies/EditPath/EditPath';
import RegionOverview from '../tab-bodies/RegionOverview/RegionOverview';

import AddNoteWithToggleActions from "./AddNoteWithToggleActions";
import AlertSettingsPageWithToggleActions from "./AlertSettingsPageWithToggleActions";
import LabelItemWithToggleActions from "./LabelItemWithToggleActions";
import LayersIcon from "../../SVG-components/layers_icon";

import TagItemWithToggleActions from "./TagItemWithToggleActions";
import VisTagsDevices from '../tab-bodies/Tags-Devices';

import styles from './FencesContainer.module.scss';

import {IProps} from "./FencesContainer.interfaces";

function FencesContainer (props: IProps): JSX.Element {
    const {style = {}} = props;

    const {itemId, itemType} = useParams<{ itemType: string; itemId: string }>();
    const isDriver = useRedux((s) => s.auth.isDriver);

    const {pathname} = fenceHistory.location;

    const dispatch = useDispatch();
    const setShow = (val: boolean) => dispatch(setGeoFenceContainerShow(val));
    const toggleShow = () => dispatch(toggleGeoFenceContainerShow());

    const isGeoFenceContainerVisible = useSelector((s: iFullStoreState) => s.fencesContainerUI.isGeoFenceContainerVisible);
    const tagView = useSelector<iFullStoreState, boolean>((s) => !!s.tagsDevicesMap.showedTagsIds.size);

    const IS_PUBLIC_VIEW = window.location.href.includes('public-view');

    useEffect(() => {
        // no redirect needed if on base
        if (pathname == '/') return;

        if (!tagView && (!itemId || !itemType)) {
            fenceHistory.push('/');
        }
    }, [tagView, itemType, itemId, pathname]);

    if (isDriver) return null;
    if (IS_PUBLIC_VIEW) return null;

    const fenceContainerClassName = [
      'fence-container',
      !isMobile ? styles.fencesContainer : '',
      !isMobile && isGeoFenceContainerVisible ? styles.fencesContainerOpened : '',
    ].join(' ');

    return (
        <Router history={fenceHistory}>
          <div
            className={fenceContainerClassName}
            style={isMobile ? {} : {...style}}
          >
            <Route path="/:tab(map|fences|vis-tag-devices)?">
              <div
                className={`${isMobile ? '' : styles.layersBtn}`}
                onClick={toggleShow}
              >
                <LayersIcon />

                {!isMobile ? <span className={styles.layersBtnText}>GeoFence</span> : null}
              </div>
            </Route>

            <Switch>
              <Route
                exact
                path="/fences"
                render={({ match }) => (
                  <FenceBody
                    itemType={itemType as ItemType}
                    itemId={itemId}
                    {...match}
                  />
                )}
              />

              <Route exact path="/vis-tag-devices" component={VisTagsDevices} />

              <Route
                exact
                path={`/:itemType/:itemId/${FenceGroup.FENCE}/:fenceId/edit`}
                render={(all): JSX.Element => (
                  <LoadFenceAsyncWrapper
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    tagView={tagView}
                    setEditMode={true}
                    itemType={itemType || 'global'}
                    itemId={itemId || 'all'}
                    comp={FenceEdit}
                    {...all}
                  />
                )}
              />

              <Route
                exact
                path={'/filters/:filterId/edit'}
                render={(): JSX.Element => (
                  <FilterEdit
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                  />
                )}
              />

              <Route
                exact
                path="/filters"
                render={(): JSX.Element => (
                  <FenceBodyLayout
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    needFencesTab={null}
                    showTabs={true}
                    activeTab={1}
                  />
                )}
              />

              <Route
                exact
                path={'/fences-tab'}
                render={() => (
                  <FenceBodyLayout
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    needFencesTab={null}
                    showTabs={true}
                    activeTab={0}
                  />
                )}
              />

              <Route
                exact
                path={`/:itemType/:itemId/${FenceGroup.PATH}/:fenceId/edit`}
                render={(all): JSX.Element => (
                  <LoadFenceAsyncWrapper
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    tagView={tagView}
                    setEditMode={true}
                    itemType={itemType}
                    itemId={itemId}
                    comp={PathEdit}
                    {...all}
                  />
                )}
              />

              <Route
                exact
                path={'/:itemType/:itemId/:fenceGroup/fence-details/:fenceId'}
                render={(all): JSX.Element => (
                  <LoadFenceAsyncWrapper
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    tagView={tagView}
                    setEditMode={true}
                    itemType={itemType}
                    itemId={itemId}
                    comp={RegionOverview}
                    {...all}
                  />
                )}
              />

              <Route
                exact
                path={'/tag-fence/:isFence/:type/:itemId'}
                render={(all): JSX.Element => <TagItemWithToggleActions {...all} />}
              />

              <Route
                exact
                path={'/label-fence/:isFence/:type/:itemId'}
                render={(all): JSX.Element => <LabelItemWithToggleActions {...all} />}
              />

              <Route
                exact
                path="/:deviceOrFence(fence)/:deviceOrFenceId/notes/:action(add|edit)/:noteId"
                render={(all): JSX.Element => <AddNoteWithToggleActions {...all} />}
              />

              <Route
                exact
                path="/:itemType/:fenceType/:deviceId/:itemId/alerts/:eventKey"
                render={(all): JSX.Element => (
                  <AlertSettingsPageWithToggleActions
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    {...all}
                  />
                )}
              />

              <Route
                exact
                path="/:tab(map|fences|vis-tag-devices)?"
                render={(): JSX.Element => (
                  <FenceBodyLayout
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    needFencesTab={null}
                    showTabs={true}
                  />
                )}
              />

              <Route
                exact
                path="/"
                render={(all): JSX.Element => (
                  <MapEmptyBodyLayout
                    {...all}
                    isVisible={isGeoFenceContainerVisible}
                    toggleVisible={setShow}
                    itemType={itemType}
                    itemId={itemId}
                  />
                )}
              />
            </Switch>
          </div>
        </Router>
    );
}

export default withSentry(FencesContainer);
